import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { PageMeta, ServiceListing, ServiceOrder } from '@/lib/types'

export interface ServiceFilters {
  q?: string
  /** ID penjual; membatasi pada jasa miliknya. */
  seller?: string
  category?: string
  skill?: string
  sort?: string
  max_price?: number
  page?: number
}

interface ServiceList {
  services: ServiceListing[]
  meta: PageMeta
}

export function useServices(filters: ServiceFilters) {
  return useQuery({
    queryKey: ['services', filters],
    queryFn: () => api.get<ServiceList>('/services', { query: { ...filters, per_page: 12 } }),
    staleTime: 60_000,
  })
}

export function useService(slug: string) {
  return useQuery({
    queryKey: ['service', slug],
    queryFn: () => api.get<{ service: ServiceListing }>(`/services/${slug}`),
    select: (data) => data.service,
  })
}

export function useServiceReviews(slug: string) {
  return useQuery({
    queryKey: ['service-reviews', slug],
    queryFn: () =>
      api.get<{ reviews: Array<{ id: string; author_name: string; rating: number; comment: string; created_at: string }> }>(
        `/services/${slug}/reviews`,
      ),
    select: (data) => data.reviews,
  })
}

export function useMyServices() {
  return useQuery({
    queryKey: ['my-services'],
    queryFn: () => api.get<ServiceList>('/me/services', { query: { per_page: 50 } }),
  })
}

export function useManagedService(listingId: string) {
  return useQuery({
    queryKey: ['managed-service', listingId],
    queryFn: () =>
      api.get<{ service: ServiceListing; readiness: { ready: boolean; reasons: string[] } }>(
        `/manage/services/${listingId}`,
      ),
  })
}

export interface ServiceInput {
  title: string
  description: string
  category: string
  skills: string[]
  thumbnail_url?: string
}

export function useCreateService() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: ServiceInput) => api.post<{ service: ServiceListing }>('/services', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['my-services'] }),
  })
}

export function useUpdateService(listingId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: ServiceInput) =>
      api.patch<{ service: ServiceListing }>(`/manage/services/${listingId}`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['managed-service', listingId] })
      client.invalidateQueries({ queryKey: ['my-services'] })
    },
  })
}

export interface PackageInput {
  tier: string
  title: string
  description?: string
  price: number
  delivery_days: number
  revisions: number
  features: string[]
}

export function useSavePackage(listingId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: PackageInput) => {
      const { tier, ...body } = input
      return api.put<void>(`/manage/services/${listingId}/packages/${tier}`, body)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['managed-service', listingId] }),
  })
}

export function useDeletePackage(listingId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (tier: string) => api.delete<void>(`/manage/services/${listingId}/packages/${tier}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['managed-service', listingId] }),
  })
}

export function usePublishService(listingId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<{ service: ServiceListing }>(`/manage/services/${listingId}/publish`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['managed-service', listingId] })
      client.invalidateQueries({ queryKey: ['my-services'] })
    },
  })
}

export function usePauseService(listingId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<{ service: ServiceListing }>(`/manage/services/${listingId}/pause`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['managed-service', listingId] })
      client.invalidateQueries({ queryKey: ['my-services'] })
    },
  })
}

export function useOrderService() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { packageId: string; requirements: string }) =>
      api.post<{ order: ServiceOrder }>(`/service-packages/${input.packageId}/order`, {
        requirements: input.requirements,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['service-orders'] })
      client.invalidateQueries({ queryKey: ['contracts'] })
    },
  })
}

export function useServiceOrders(role?: 'buyer' | 'seller') {
  return useQuery({
    queryKey: ['service-orders', role],
    queryFn: () => api.get<{ orders: ServiceOrder[]; meta: PageMeta }>('/me/service-orders', {
      query: { role, per_page: 50 },
    }),
  })
}

export function useReviewServiceOrder() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { orderId: string; rating: number; comment: string }) =>
      api.post<unknown>(`/service-orders/${input.orderId}/review`, {
        rating: input.rating,
        comment: input.comment,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['service-orders'] }),
  })
}
